import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import type { UnitType } from "../../../lib/api";
import { useAppPreferences } from "../../../lib/preferences";
import { Section } from "../ui";
import { normalizePromotionCode, type PromotionConfiguration, type PromotionVenueOption } from "./types";
import "./promotions.css";

export const PROMOTION_UNIT_TYPES: UnitType[] = [
  "console",
  "pc",
  "vr",
  "simulator",
  "billiards",
  "pingpong",
  "room",
  "other",
];

const WEEKDAYS = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localInputToIso(value: string): string {
  return new Date(value).toISOString();
}

function isPositiveDecimal(value: string): boolean {
  if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) return false;
  return Number(value) > 0;
}

// The API accepts only canonical decimals ("50.00", "12.5"), so normalize
// validated input instead of sending it as typed ("050", "50", "12.5").
function canonicalDecimal(value: string, fixedDecimals: boolean): string {
  const [whole = "", fraction = ""] = value.trim().split(".");
  const integer = whole.replace(/^0+(?=\d)/, "");
  if (fixedDecimals) return `${integer}.${fraction.padEnd(2, "0")}`;
  return fraction ? `${integer}.${fraction}` : integer;
}

function isPositiveInteger(value: string): boolean {
  return /^\d+$/.test(value.trim()) && Number(value) > 0;
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

interface Draft {
  name_en: string;
  name_ar: string;
  offer_mode: "auto" | "code";
  code: string;
  discount_type: "percent" | "fixed";
  discount_percent: string;
  fixed_amount: string;
  max_discount_amount: string;
  starts_at: string;
  ends_at: string;
  venue_ids: string[];
  unit_types: UnitType[];
  weekdays: number[];
  local_start_time: string;
  local_end_time: string;
  first_booking_only: boolean;
  budget_amount: string;
  max_uses: string;
  per_customer_max_uses: string;
}

function toDraft(initial: PromotionConfiguration): Draft {
  return {
    name_en: initial.name_en,
    name_ar: initial.name_ar,
    offer_mode: initial.offer_mode,
    code: initial.code ?? "",
    discount_type: initial.discount_type,
    discount_percent: initial.discount_percent ?? "",
    fixed_amount: initial.fixed_amount ?? "",
    max_discount_amount: initial.max_discount_amount ?? "",
    starts_at: isoToLocalInput(initial.starts_at),
    ends_at: initial.ends_at ? isoToLocalInput(initial.ends_at) : "",
    venue_ids: [...initial.venue_ids],
    unit_types: [...initial.unit_types],
    weekdays: [...initial.weekdays],
    local_start_time: initial.local_start_time ?? "",
    local_end_time: initial.local_end_time ?? "",
    first_booking_only: initial.first_booking_only,
    budget_amount: initial.budget_amount ?? "",
    max_uses: initial.max_uses === null || initial.max_uses === undefined ? "" : String(initial.max_uses),
    per_customer_max_uses:
      initial.per_customer_max_uses === null || initial.per_customer_max_uses === undefined
        ? ""
        : String(initial.per_customer_max_uses),
  };
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

/** Advanced targeting/limits differ from the open-by-default campaign. */
function isAdvancedDirty(initial: PromotionConfiguration): boolean {
  return (
    initial.venue_ids.length > 0 ||
    initial.unit_types.length > 0 ||
    initial.weekdays.length > 0 ||
    initial.local_start_time !== null ||
    initial.local_end_time !== null ||
    initial.first_booking_only ||
    initial.budget_amount !== null ||
    initial.max_uses !== null ||
    initial.per_customer_max_uses !== null
  );
}

/**
 * One labeled control. Help and error text sit outside the <label> so the
 * accessible name stays exactly the field name.
 */
function Field({
  children,
  error,
  help,
  id,
  label,
}: {
  children: ReactNode;
  error?: string;
  help?: ReactNode;
  id: string;
  label: ReactNode;
}) {
  const helpId = `${id}-help`;
  return (
    <div className="ops-field">
      <label className="ops-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {help ? (
        <span className="console-muted" id={helpId}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="console-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function PromotionCampaignForm({
  busy = false,
  canEdit,
  expectedRevisionId = null,
  initial,
  onCancel,
  onSubmit,
  submitLabel,
  unitTypeOptions = PROMOTION_UNIT_TYPES,
  venueOptions,
}: {
  busy?: boolean;
  canEdit: boolean;
  /** Revision being revised. Shown so the editor knows accepted bookings keep their terms. */
  expectedRevisionId?: string | null;
  initial: PromotionConfiguration;
  onCancel?: () => void;
  onSubmit: (payload: PromotionConfiguration) => void;
  submitLabel: string;
  unitTypeOptions?: UnitType[];
  venueOptions: PromotionVenueOption[];
}) {
  const { t } = useAppPreferences();
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(() => isAdvancedDirty(initial));
  const formId = useId();
  const idFor = (field: string) => `${formId}-${field}`;
  const advancedId = `${formId}-advanced`;

  useEffect(() => {
    setDraft(toDraft(initial));
    setErrors({});
    setAdvancedOpen(isAdvancedDirty(initial));
  }, [initial]);

  const disabled = !canEdit || busy;
  const timeZone = browserTimeZone();

  // Live summary of the advanced group, so collapsed fields never hide
  // surprises: unlimited reads as unlimited, never as capped.
  const dayName = (value: number) => WEEKDAYS.find((day) => day.value === value)?.label ?? String(value);
  const advancedParts = [
    draft.venue_ids.length === 0 ? t("Everywhere") : t("{count} venues", { count: draft.venue_ids.length }),
    draft.unit_types.length === 0 ? t("Everything") : draft.unit_types.map((type) => t(type)).join(", "),
    draft.weekdays.length === 0
      ? t("Every day")
      : [...draft.weekdays].sort((a, b) => a - b).map((day) => t(dayName(day))).join(", "),
    !draft.local_start_time && !draft.local_end_time
      ? t("All day")
      : `${draft.local_start_time || "–"}–${draft.local_end_time || "–"}`,
    draft.budget_amount.trim() ? `${draft.budget_amount.trim()} EGP` : t("No limit"),
    draft.max_uses.trim() || draft.per_customer_max_uses.trim()
      ? `${t("Total uses (optional)")}: ${draft.max_uses.trim() || t("No limit")} · ${t("Uses per customer (optional)")}: ${draft.per_customer_max_uses.trim() || t("No limit")}`
      : t("No limit"),
  ];
  if (draft.first_booking_only) advancedParts.push(t("First booking only"));
  const advancedIsDefault =
    draft.venue_ids.length === 0 &&
    draft.unit_types.length === 0 &&
    draft.weekdays.length === 0 &&
    !draft.local_start_time &&
    !draft.local_end_time &&
    !draft.first_booking_only &&
    !draft.budget_amount.trim() &&
    !draft.max_uses.trim() &&
    !draft.per_customer_max_uses.trim();
  const summary = advancedIsDefault
    ? t("Applies everywhere, every day, with no limits.")
    : advancedParts.join(" · ");
  const set = <FieldName extends keyof Draft>(field: FieldName, value: Draft[FieldName]) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    const next: Record<string, string> = {};
    if (!draft.name_en.trim()) next.name_en = t("Enter the English offer name.");
    if (!draft.name_ar.trim()) next.name_ar = t("Enter the Arabic offer name.");
    if (draft.offer_mode === "code" && !normalizePromotionCode(draft.code)) {
      next.code = t("Enter the code players will type.");
    }
    if (draft.discount_type === "percent") {
      if (!draft.discount_percent.trim() || !isPositiveDecimal(draft.discount_percent)) {
        next.discount_percent = t("Enter a percent above zero, for example 10 or 12.5.");
      } else if (Number(draft.discount_percent) > 100) {
        next.discount_percent = t("Enter a percent of 100 or less.");
      }
    } else if (!draft.fixed_amount.trim() || !isPositiveDecimal(draft.fixed_amount)) {
      next.fixed_amount = t("Enter a fixed amount in EGP above zero.");
    }
    if (draft.max_discount_amount.trim() && !isPositiveDecimal(draft.max_discount_amount)) {
      next.max_discount_amount = t("Enter a cap in EGP above zero, or leave it empty.");
    }
    if (!draft.starts_at) {
      next.starts_at = t("Choose when the offer starts.");
    }
    if (draft.ends_at && draft.starts_at && draft.ends_at <= draft.starts_at) {
      next.ends_at = t("The end must be after the start.");
    }
    if (draft.local_start_time && !/^\d{2}:\d{2}$/.test(draft.local_start_time)) {
      next.local_start_time = t("Use 24-hour HH:mm.");
    }
    if (draft.local_end_time && !/^\d{2}:\d{2}$/.test(draft.local_end_time)) {
      next.local_end_time = t("Use 24-hour HH:mm.");
    }
    if ((draft.local_start_time && !draft.local_end_time) || (!draft.local_start_time && draft.local_end_time)) {
      const message = t("Enter both the daily start and end, or leave both empty.");
      if (!draft.local_start_time) next.local_start_time = message;
      if (!draft.local_end_time) next.local_end_time = message;
    } else if (
      draft.local_start_time &&
      draft.local_end_time &&
      draft.local_start_time === draft.local_end_time
    ) {
      next.local_end_time = t("Daily start and end must differ.");
    }
    if (draft.budget_amount.trim() && !isPositiveDecimal(draft.budget_amount)) {
      next.budget_amount = t("Enter a budget in EGP above zero, or leave it empty for no budget limit.");
    }
    if (draft.max_uses.trim() && !isPositiveInteger(draft.max_uses)) {
      next.max_uses = t("Enter a whole number above zero, or leave it empty for unlimited uses.");
    }
    if (draft.per_customer_max_uses.trim() && !isPositiveInteger(draft.per_customer_max_uses)) {
      next.per_customer_max_uses = t("Enter a whole number above zero, or leave it empty for no per-customer limit.");
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      // Invalid hidden inputs must surface: open the advanced group instead
      // of leaving errors out of view.
      if (
        next.local_start_time ||
        next.local_end_time ||
        next.budget_amount ||
        next.max_uses ||
        next.per_customer_max_uses
      ) {
        setAdvancedOpen(true);
      }
      return;
    }

    onSubmit({
      name_en: draft.name_en.trim(),
      name_ar: draft.name_ar.trim(),
      offer_mode: draft.offer_mode,
      code: draft.offer_mode === "code" ? normalizePromotionCode(draft.code) : null,
      discount_type: draft.discount_type,
      discount_percent: draft.discount_type === "percent" ? canonicalDecimal(draft.discount_percent, false) : null,
      fixed_amount: draft.discount_type === "fixed" ? canonicalDecimal(draft.fixed_amount, true) : null,
      max_discount_amount: draft.max_discount_amount.trim() ? canonicalDecimal(draft.max_discount_amount, true) : null,
      starts_at: localInputToIso(draft.starts_at),
      ends_at: draft.ends_at ? localInputToIso(draft.ends_at) : null,
      venue_ids: [...draft.venue_ids],
      unit_types: [...draft.unit_types],
      weekdays: [...draft.weekdays],
      local_start_time: draft.local_start_time ? draft.local_start_time : null,
      local_end_time: draft.local_end_time ? draft.local_end_time : null,
      first_booking_only: draft.first_booking_only,
      budget_amount: draft.budget_amount.trim() ? canonicalDecimal(draft.budget_amount, true) : null,
      max_uses: draft.max_uses.trim() ? Number(draft.max_uses) : null,
      per_customer_max_uses: draft.per_customer_max_uses.trim() ? Number(draft.per_customer_max_uses) : null,
    });
  };

  return (
    <form className="promo-form" onSubmit={handleSubmit}>
      {expectedRevisionId ? (
        <p className="ops-banner" data-tone="info">
          {t("You are revising revision {revision}. New terms apply to future bookings. Accepted holds keep the terms they were given.", {
            revision: expectedRevisionId,
          })}
        </p>
      ) : null}
      {!canEdit ? (
        <p className="ops-banner" data-tone="info">
          {t("Support accounts can read campaigns. Only admins can create or change them.")}
        </p>
      ) : null}

      <Section description={t("Players see the offer name next to the original charge and the savings.")} title={t("Offer")}>
        <div className="promo-grid">
          <Field error={errors.name_en} id={idFor("name-en")} label={t("Name (English)")}>
            <input
              aria-invalid={Boolean(errors.name_en)}
              className="ops-input"
              disabled={disabled}
              id={idFor("name-en")}
              maxLength={120}
              onChange={(event) => set("name_en", event.target.value)}
              value={draft.name_en}
            />
          </Field>
          <Field error={errors.name_ar} id={idFor("name-ar")} label={t("Name (Arabic)")}>
            <input
              aria-invalid={Boolean(errors.name_ar)}
              className="ops-input"
              disabled={disabled}
              id={idFor("name-ar")}
              maxLength={120}
              onChange={(event) => set("name_ar", event.target.value)}
              value={draft.name_ar}
            />
          </Field>
          <Field id={idFor("offer-mode")} label={t("How players get it")}>
            <select
              className="ops-select"
              disabled={disabled}
              id={idFor("offer-mode")}
              onChange={(event) => set("offer_mode", event.target.value as Draft["offer_mode"])}
              value={draft.offer_mode}
            >
              <option value="auto">{t("Automatic")}</option>
              <option value="code">{t("Code")}</option>
            </select>
          </Field>
          {draft.offer_mode === "code" ? (
            <Field
              error={errors.code}
              help={t("Saved as uppercase. Automatic offers have no code.")}
              id={idFor("code")}
              label={t("Code")}
            >
              <input
                aria-invalid={Boolean(errors.code)}
                className="ops-input promo-code-input"
                disabled={disabled}
                id={idFor("code")}
                maxLength={32}
                onChange={(event) => set("code", event.target.value)}
                placeholder="WELCOME10"
                value={draft.code}
              />
            </Field>
          ) : null}
        </div>
      </Section>

      <Section description={t("One offer applies per booking. The server checks that the savings fit the campaign funding.")} title={t("Savings")}>
        <div className="promo-grid">
          <Field id={idFor("discount-type")} label={t("Discount type")}>
            <select
              className="ops-select"
              disabled={disabled}
              id={idFor("discount-type")}
              onChange={(event) => set("discount_type", event.target.value as Draft["discount_type"])}
              value={draft.discount_type}
            >
              <option value="percent">{t("Percent")}</option>
              <option value="fixed">{t("Fixed amount (EGP)")}</option>
            </select>
          </Field>
          {draft.discount_type === "percent" ? (
            <Field error={errors.discount_percent} id={idFor("percent")} label={t("Percent")}>
              <input
                aria-invalid={Boolean(errors.discount_percent)}
                className="ops-input"
                disabled={disabled}
                id={idFor("percent")}
                inputMode="decimal"
                onChange={(event) => set("discount_percent", event.target.value)}
                placeholder="10"
                value={draft.discount_percent}
              />
            </Field>
          ) : (
            <Field error={errors.fixed_amount} id={idFor("fixed")} label={t("Fixed amount")}>
              <input
                aria-invalid={Boolean(errors.fixed_amount)}
                className="ops-input"
                disabled={disabled}
                id={idFor("fixed")}
                inputMode="decimal"
                onChange={(event) => set("fixed_amount", event.target.value)}
                placeholder="25.00"
                value={draft.fixed_amount}
              />
            </Field>
          )}
          <Field error={errors.max_discount_amount} id={idFor("cap")} label={t("Maximum saving per booking (EGP, optional)")}>
            <input
              aria-invalid={Boolean(errors.max_discount_amount)}
              className="ops-input"
              disabled={disabled}
              id={idFor("cap")}
              inputMode="decimal"
              onChange={(event) => set("max_discount_amount", event.target.value)}
              placeholder={t("No cap")}
              value={draft.max_discount_amount}
            />
          </Field>
        </div>
      </Section>

      <Section description={t("Start and end are exact moments saved as UTC. Enter them in your browser time; days and hours match each booking in its own venue time.")} title={t("Schedule")}>
        <div className="promo-grid">
          <Field
            error={errors.starts_at}
            help={timeZone ? t("Your browser time is {zone}.", { zone: timeZone }) : undefined}
            id={idFor("starts")}
            label={t("Starts at")}
          >
            <input
              aria-invalid={Boolean(errors.starts_at)}
              className="ops-input"
              disabled={disabled}
              id={idFor("starts")}
              onChange={(event) => set("starts_at", event.target.value)}
              type="datetime-local"
              value={draft.starts_at}
            />
          </Field>
          <Field
            error={errors.ends_at}
            help={t("Empty means the offer stays open until paused or exhausted.")}
            id={idFor("ends")}
            label={t("Ends at (optional)")}
          >
            <input
              aria-invalid={Boolean(errors.ends_at)}
              className="ops-input"
              disabled={disabled}
              id={idFor("ends")}
              onChange={(event) => set("ends_at", event.target.value)}
              type="datetime-local"
              value={draft.ends_at}
            />
          </Field>
        </div>
      </Section>

      <Section
        description={summary}
        title={
          <button
            aria-controls={advancedId}
            aria-expanded={advancedOpen}
            className="console-link promo-name-button"
            onClick={() => setAdvancedOpen((open) => !open)}
            type="button"
          >
            {advancedOpen ? t("Hide") : t("Show")}: {t("Targeting and limits (optional)")}
          </button>
        }
      >
        <div className={advancedOpen ? undefined : "promo-collapsed"} id={advancedId}>
        <fieldset className="promo-fieldset">
          <legend className="ops-label">{t("Days (optional)")}</legend>
          <p className="console-muted">{t("Empty means every day.")}</p>
          <div className="promo-checks">
            {WEEKDAYS.map((day) => (
              <label className="ops-checkbox-row" key={day.value}>
                <input
                  checked={draft.weekdays.includes(day.value)}
                  disabled={disabled}
                  onChange={() => set("weekdays", toggle(draft.weekdays, day.value))}
                  type="checkbox"
                />
                <span>{t(day.label)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="promo-grid">
          <Field error={errors.local_start_time} id={idFor("from")} label={t("Daily from (optional)")}>
            <input
              aria-invalid={Boolean(errors.local_start_time)}
              className="ops-input"
              disabled={disabled}
              id={idFor("from")}
              onChange={(event) => set("local_start_time", event.target.value)}
              type="time"
              value={draft.local_start_time}
            />
          </Field>
          <Field error={errors.local_end_time} id={idFor("until")} label={t("Daily until (optional)")}>
            <input
              aria-invalid={Boolean(errors.local_end_time)}
              className="ops-input"
              disabled={disabled}
              id={idFor("until")}
              onChange={(event) => set("local_end_time", event.target.value)}
              type="time"
              value={draft.local_end_time}
            />
          </Field>
        </div>
        <p className="console-muted">{t("Empty selections mean everywhere and everything. Days and hours always follow each booking's own venue time, even when several venues are selected.")}</p>
        <fieldset className="promo-fieldset">
          <legend className="ops-label">{t("Venues (optional)")}</legend>
          {venueOptions.length === 0 ? (
            <p className="console-muted">{t("No venues to choose from yet. Saving without a selection applies everywhere.")}</p>
          ) : (
            <div className="promo-checks">
              {venueOptions.map((venue) => (
                <label className="ops-checkbox-row" key={venue.id}>
                  <input
                    checked={draft.venue_ids.includes(venue.id)}
                    disabled={disabled}
                    onChange={() => set("venue_ids", toggle(draft.venue_ids, venue.id))}
                    type="checkbox"
                  />
                  <span>{venue.name}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <fieldset className="promo-fieldset">
          <legend className="ops-label">{t("Unit types (optional)")}</legend>
          <div className="promo-checks">
            {unitTypeOptions.map((type) => (
              <label className="ops-checkbox-row" key={type}>
                <input
                  checked={draft.unit_types.includes(type)}
                  disabled={disabled}
                  onChange={() => set("unit_types", toggle(draft.unit_types, type))}
                  type="checkbox"
                />
                <span>{t(type)}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <p className="console-muted">{t("Budgets and use limits count across all revisions. Lowering them never changes already accepted bookings.")}</p>
        <div className="promo-grid">
          <Field
            error={errors.budget_amount}
            help={t("Limits include held bookings, not only completed ones.")}
            id={idFor("budget")}
            label={t("Budget (EGP, optional)")}
          >
            <input
              aria-invalid={Boolean(errors.budget_amount)}
              className="ops-input"
              disabled={disabled}
              id={idFor("budget")}
              inputMode="decimal"
              onChange={(event) => set("budget_amount", event.target.value)}
              placeholder={t("No limit")}
              value={draft.budget_amount}
            />
          </Field>
          <Field error={errors.max_uses} id={idFor("max-uses")} label={t("Total uses (optional)")}>
            <input
              aria-invalid={Boolean(errors.max_uses)}
              className="ops-input"
              disabled={disabled}
              id={idFor("max-uses")}
              inputMode="numeric"
              onChange={(event) => set("max_uses", event.target.value)}
              placeholder={t("No limit")}
              value={draft.max_uses}
            />
          </Field>
          <Field error={errors.per_customer_max_uses} id={idFor("per-customer")} label={t("Uses per customer (optional)")}>
            <input
              aria-invalid={Boolean(errors.per_customer_max_uses)}
              className="ops-input"
              disabled={disabled}
              id={idFor("per-customer")}
              inputMode="numeric"
              onChange={(event) => set("per_customer_max_uses", event.target.value)}
              placeholder={t("No limit")}
              value={draft.per_customer_max_uses}
            />
          </Field>
        </div>
        <div className="promo-check-alone">
          <label className="ops-checkbox-row" htmlFor={idFor("first-only")}>
            <input
              checked={draft.first_booking_only}
              disabled={disabled}
              id={idFor("first-only")}
              onChange={(event) => set("first_booking_only", event.target.checked)}
              type="checkbox"
            />
            <span>{t("First booking only")}</span>
          </label>
          <p className="console-muted">{t("First means the first completed, fully paid booking. An accepted hold keeps its eligibility.")}</p>
        </div>
        </div>
      </Section>

      <div className="console-button-row">
        {canEdit ? (
          <button className="ops-button" data-variant="primary" disabled={busy} type="submit">
            {busy ? t("Saving...") : submitLabel}
          </button>
        ) : null}
        {onCancel ? (
          <button className="ops-button" data-variant="secondary" disabled={busy} onClick={onCancel} type="button">
            {t("Cancel")}
          </button>
        ) : null}
      </div>
    </form>
  );
}
